import { Heading } from '@/components/heading';
import commonMessages from '@/lang/messages/common';
import { routes } from '@/utils/routes';
import { Check } from '@mui/icons-material';
import { BottomAction } from '@rhg/components/bottom-action';
import { EmptyHeader } from '@rhg/components/header';
import IconWithWhiteCircle from '@rhg/components/ui/icon-with-white-circle';
import { useIsMobile } from '@rhg/hooks/use-is-mobile';
import { logger } from '@rhg/libs/logger';
import { Dialog, Theme, Box } from '@rhg/libs/material';
import { useRouter } from 'next/router';
import { useState, useId } from 'react';
import { useIntl } from 'react-intl';
import messages from '../../messages';

const styleBackground = {
    background: (theme: Theme) => theme.palette.gradient.backgroundPrimary,
};

interface DoubleOptInConfirmDialogProps {
    isDialogOpen: boolean;
    /**
     * Optional callback when dialog is closed.
     * When provided, prevents the default redirect to dashboard.
     */
    onClose?: () => void;
}

export default function DoubleOptInConfirmDialog({
    isDialogOpen: isDialogOpenFromProps,
    onClose,
}: DoubleOptInConfirmDialogProps) {
    const { formatMessage } = useIntl();
    const { push } = useRouter();
    const isMobile = useIsMobile('lg');
    const id = useId();
    const [isOpen, setIsOpen] = useState(isDialogOpenFromProps);

    const handleClose = () => {
        setIsOpen(false);
        if (onClose) {
            onClose();
            return;
        }

        push(routes.dashboard).catch((err) =>
            logger.error('Failed to navigate to dashboard', {}, err as Error)
        );
    };

    return (
        <Dialog
            trackDialog="EmailPreferencesConfirmed"
            trackScreen="EmailPreferencesConfirmed"
            trackFeature="UKCanada"
            fullScreen
            open={isOpen}
            aria-labelledby={id}
            PaperProps={{
                sx: styleBackground,
            }}
        >
            <EmptyHeader />
            <Box sx={{ mt: 22 }}>
                <IconWithWhiteCircle>
                    <Check color="success" fontSize="large" />
                </IconWithWhiteCircle>
            </Box>
            <Heading
                titleId={id}
                title={formatMessage(
                    messages['notifications.dialog.double_opt_in.confirm.title']
                )}
                subtitle={formatMessage(
                    messages[
                        'notifications.dialog.double_opt_in.confirm.subtitle'
                    ]
                )}
                titleVariant={isMobile ? 'h5' : 'h4'}
                titleAlign="center"
                subtitleAlign="center"
            />
            <BottomAction
                trackButton="Close"
                label={formatMessage(commonMessages['common.ok'])}
                onClick={handleClose}
                color="primary"
                variant="contained"
            />
        </Dialog>
    );
}
